/**
 * RW-2531 — ONE definition of "what kind of file is this?" for every
 * attachment surface: feed posts, comments, and chat.
 *
 * These predicates used to live in `components/chat/chatUtils.js` and were
 * reachable only from chat. Comments grew its own weaker, MIME-only copy, so a
 * comment attachment and a chat attachment could already disagree about
 * whether the same file was an image. This module is the single definition;
 * `chatUtils` re-exports it so the ~18 chat call sites keep working unchanged.
 *
 * The two image predicates are deliberately DISTINCT and both are needed:
 *   isImageType    — "is this an image?"           (HEIC: yes)
 *   canPreviewImage — "can an <img> render it?"    (HEIC: no, outside Safari)
 * Render chains must branch on canPreviewImage; anything else belongs in a
 * file card. See RW-2494 for the listing-side version of the same rule.
 */

/** Format file size */
export function fmtSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

/** Check if file is image. HEIC/HEIF ARE images — they just aren't previewable. */
export function isImageType(type, name) {
  if (type?.startsWith('image/')) return true;
  return /\.(jpg|jpeg|png|gif|webp|svg|heic|heif)$/i.test(name || '');
}

/** Check if file is video */
export function isVideoType(type, name) {
  if (type?.startsWith('video/')) return true;
  return /\.(mp4|mov|webm)$/i.test(name || '');
}

/** Check if browser can preview the image */
export function canPreviewImage(type, name) {
  if (/heic|heif/i.test(type || '') || /\.(heic|heif)$/i.test(name || '')) return false;
  return isImageType(type, name);
}

/** File icon by extension */
export function getFileIcon(name) {
  const ext = (name || '').split('.').pop().toLowerCase();
  const icons = { pdf: '📄', doc: '📝', docx: '📝', xls: '📊', xlsx: '📊', csv: '📊', ppt: '📰', pptx: '📰', txt: '📃', zip: '📦', rar: '📦', mp3: '🎵', wav: '🎵', mp4: '🎬', mov: '🎬' };
  return icons[ext] || '📁';
}

/* ─── RW-2531: what a feed post may carry ─────────────────────────────────
 * A post used to accept strictly less than a comment on that same post.
 * These lists are the one place that changes.
 */

/** Documents + archives a post may carry, as file extensions. */
const DOC_EXT = /\.(pdf|docx?|xlsx?|pptx?|csv|txt|zip)$/i;

/**
 * Extension → MIME, used when the browser hands us a file with an empty
 * `type`. Chrome on Windows reports '' for .heic, and several OSes report ''
 * for .csv and .zip. We must resolve a real content type before uploading:
 * storage.rules enforces an allowlist on `request.resource.contentType`, and
 * `resizeUploadedImage` only fires when the stored type starts with `image/`.
 */
const EXT_MIME = {
  heic: 'image/heic',
  heif: 'image/heif',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  webm: 'video/webm',
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  csv: 'text/csv',
  txt: 'text/plain',
  zip: 'application/zip',
};

/**
 * The content type to STORE for a picked file. Prefers what the browser
 * reported; falls back to the extension; '' when we can identify neither —
 * which is a rejection, not an octet-stream, because `application/octet-stream`
 * would be a hole big enough to drive any renamed file through.
 */
export function contentTypeFor(file) {
  if (!file) return '';
  if (file.type) return file.type;
  const ext = (file.name || '').split('.').pop().toLowerCase();
  return EXT_MIME[ext] || '';
}

/**
 * RW-2827: what a deal document may be — PDF, Word, PNG, JPEG. MIRRORS the
 * `stDealDocType()` list in storage.rules; change them together.
 */
export const DEAL_DOC_MIMES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/png',
  'image/jpeg',
];
export const DEAL_DOC_ACCEPT = '.pdf,.png,.jpg,.jpeg,.doc,.docx';
/** storage.rules: `request.resource.size < 50 MB` on deals/…/docs. */
export const DEAL_DOC_MAX_BYTES = 50 * 1024 * 1024;

/**
 * The content type to store for a deal document, or '' when it isn't one we
 * take. Unlike contentTypeFor, a browser-reported type outside the list (an
 * empty one, or `application/octet-stream` for a .docx) falls back to the
 * extension, and the upload always sends this explicitly.
 */
export function dealDocContentType(file) {
  if (!file) return '';
  const reported = (file.type || '').split(';')[0].trim().toLowerCase();
  if (DEAL_DOC_MIMES.includes(reported)) return reported;
  const ext = (file.name || '').split('.').pop().toLowerCase();
  const byExt = EXT_MIME[ext] || '';
  return DEAL_DOC_MIMES.includes(byExt) ? byExt : '';
}

/** `accept` for the post photo picker. HEIC/HEIF are named explicitly because
 *  on several platforms `image/*` alone greys out .heic in the OS dialog. */
export const POST_IMAGE_ACCEPT = 'image/*,.heic,.heif';

/** `accept` for the post document picker — docs + archives. */
export const POST_FILE_ACCEPT = [
  '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.csv', '.txt', '.zip',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/csv',
  'text/plain',
  'application/zip',
].join(',');

/** Everything one attachment picker may take: images (HEIC named explicitly)
 *  plus documents and archives. Comments and posts use the SAME list — posts
 *  accepting less than a comment on that same post is the bug RW-2531 fixed,
 *  and two lists is how it comes back. */
export const ATTACHMENT_ACCEPT = `${POST_IMAGE_ACCEPT},${POST_FILE_ACCEPT}`;

/** Per-file ceilings. All three are mirrored by storage.rules on /posts/**;
 *  keep the two in step or a user gets an opaque upload failure instead of a
 *  sentence telling them what to do. */
export const MAX_ATTACHMENT_IMAGE_BYTES = 50 * 1024 * 1024;
export const MAX_ATTACHMENT_VIDEO_BYTES = 500 * 1024 * 1024;
export const MAX_ATTACHMENT_FILE_BYTES = 25 * 1024 * 1024;

/** How many documents one post may carry. */
export const MAX_POST_FILES = 10;

/** Is this a document/archive a post or comment may carry? */
export function isPostDocType(type, name) {
  if (DOC_EXT.test(name || '')) return true;
  const mime = type || '';
  return /^(application\/(pdf|msword|zip|x-zip-compressed|vnd\.(ms-(excel|powerpoint)|openxmlformats-officedocument\.[a-z]+\.[a-z]+)))$|^text\/(csv|plain)$/i.test(mime);
}

/**
 * Why this file can't be attached — or null when it can.
 *
 * Returns the sentence the user sees, so a refusal is never silently
 * swallowed the way the feed's video picker drops an oversize file today.
 * `accept` is advisory and drag-and-drop ignores it entirely, so this is the
 * real gate; the storage.rules guard is the one behind it.
 */
export function attachmentRejection(file) {
  if (!file || !file.name) return 'That file could not be read.';
  const name = file.name;
  if (!file.size) return `${name} is empty.`;

  const type = contentTypeFor(file);
  if (!type) return `${name} has an unrecognised file type.`;

  if (isImageType(type, name)) {
    return file.size > MAX_ATTACHMENT_IMAGE_BYTES
      ? `${name} is ${fmtSize(file.size)} — images are limited to ${fmtSize(MAX_ATTACHMENT_IMAGE_BYTES)}.`
      : null;
  }
  if (isVideoType(type, name)) {
    return file.size > MAX_ATTACHMENT_VIDEO_BYTES
      ? `${name} is ${fmtSize(file.size)} — videos are limited to ${fmtSize(MAX_ATTACHMENT_VIDEO_BYTES)}.`
      : null;
  }
  if (isPostDocType(type, name)) {
    return file.size > MAX_ATTACHMENT_FILE_BYTES
      ? `${name} is ${fmtSize(file.size)} — files are limited to ${fmtSize(MAX_ATTACHMENT_FILE_BYTES)}.`
      : null;
  }
  return `${name} isn't a supported file type. You can attach images, video, PDF, Word, Excel, PowerPoint, CSV, TXT and ZIP.`;
}

/**
 * Screen a picked FileList: the files that may be attached, and one sentence
 * per file that may not. Shared by the post composer and the comment box so
 * the two cannot drift back apart.
 */
export function screenAttachments(fileList) {
  const accepted = [];
  const errors = [];
  for (const file of Array.from(fileList || [])) {
    const why = attachmentRejection(file);
    if (why) errors.push(why);
    else accepted.push(file);
  }
  return { accepted, errors };
}
